import React, { useContext, useState } from "react";
import { View, StyleSheet } from "react-native";
import moment from "moment-timezone";
import NtListRow from "../../components/NtListRow";
import NtListActionButton from "../../components/NtListActionButton";
import NtBadge from "../../components/NtBadge";
import NtAudioPlayer from "../../components/NtAudioPlayer";
import UserContext from "../../context/UserContext";
import { useTheme } from "../../context/ThemeContext";
import Logger from "../../common/utils/Logger";
import {
  parseDisplayDate,
  showToast,
} from "../../common/utils/ControllerUtils";
import {
  fetchVoicemailAudio,
  markVoicemailMessageAsRead,
} from "../../api/voicemail";

function NtVoicemailMessageItem({
  item,
  columns,
  extension,
  onDelete,
  canEdit,
}) {
  const context = useContext(UserContext);
  const { theme } = useTheme();

  // Track read state locally so the badge flips immediately when the user
  // starts playing — without waiting for a list refetch. Initial value comes
  // from the server's `read_epoch` field (set when the user previously
  // played the message). `markedRead` is a one-shot optimistic flag that
  // covers the time between "user hit play" and "next list refetch".
  const [markedRead, setMarkedRead] = useState(false);
  const isRead = !!item.read_epoch || markedRead;

  // Fetch the audio + decode it from base64 to a Blob. Returned Blob is
  // reused by the player (which needs to URL.createObjectURL it) and the
  // download handler (which needs to feed it to an <a download> trigger).
  const fetchAudioBlob = async () => {
    const res = await fetchVoicemailAudio(
      context.user?.domain,
      extension,
      item.voicemail_message_uuid,
    );
    Logger("Voicemail audio fetch response", res);
    if (!res.ok || !res.data) return null;
    try {
      const binary = atob(res.data);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      return new Blob([bytes], { type: "audio/mpeg" });
    } catch (e) {
      Logger("Voicemail audio: base64 decode failed", e);
      return null;
    }
  };

  const loadAudio = fetchAudioBlob;

  const handleDownload = async () => {
    if (typeof document === "undefined") return;
    const blob = await fetchAudioBlob();
    if (!blob) {
      showToast("Download failed", "Couldn't fetch the voicemail audio.", "error");
      return;
    }
    // Format filename like voicemail-2026-05-13-from-3055551234.mp3 for
    // readability. Falls back to the message UUID if metadata is missing.
    const dateStr = item.created_epoch
      ? moment.unix(item.created_epoch).format("YYYY-MM-DD-HHmm")
      : "voicemail";
    const caller = (item.caller_id_number || item.caller_id_name || "unknown")
      .toString()
      .replace(/[^a-zA-Z0-9-]/g, "");
    const filename = `voicemail-${dateStr}-from-${caller}.mp3`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handlePlayStarted = async () => {
    // Only fire once per session. If the message was already read or we've
    // already flipped the local flag, skip.
    if (isRead) return;
    setMarkedRead(true); // optimistic
    const res = await markVoicemailMessageAsRead(
      context.user?.domain,
      extension,
      item.voicemail_message_uuid,
    );
    Logger("Voicemail mark-as-read response", res);
    if (!res.ok) {
      // Revert the optimistic flag so the New badge comes back.
      setMarkedRead(false);
    }
  };

  return (
    <View style={styles.container}>
      <NtListRow
        item={item}
        columns={columns}
        renderCustom={(row, column) => {
          if (column == "message_length") return `${row[column]}s`;
          if (column == "created_epoch") {
            const date = moment.unix(row[column]).toDate();
            return parseDisplayDate(date);
          }
          if (column == "read_epoch") {
            return (
              <NtBadge variant={isRead ? "gray" : "blue"}>
                {isRead ? "Read" : "New"}
              </NtBadge>
            );
          }
          if (column == "player") {
            return (
              <NtAudioPlayer
                loadAudio={loadAudio}
                onPlayStarted={handlePlayStarted}
              />
            );
          }
        }}
        renderLeadingAction={
          <NtListActionButton
            icon="download-outline"
            onPress={handleDownload}
          />
        }
        onActionIcon="delete"
        onActionColor={theme.red}
        onActionPress={canEdit && onDelete ? () => onDelete(item) : null}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {},
});

export default NtVoicemailMessageItem;
